import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDriverAuth, useDriverSession, useDriverOffers, useActiveTrip } from '../../lib/driver-hooks';
import { Power, MapPin, Navigation, Check, X, Car, Package, CheckCircle2, LogOut, Loader2, Wifi, WifiOff, Bug, RefreshCw, ChevronDown, ChevronUp, Phone, MessageCircle, Map as MapIcon, Clock, CornerUpLeft, CornerUpRight, ArrowUp } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { EmbeddedMap } from '../../components/driver/EmbeddedMap';
import { StaticRouteMap } from '../../components/driver/StaticRouteMap';
import { NavigationMap } from '../../components/driver/NavigationMap';
import type { RouteStep } from '../../lib/routing';
// ─── Map Error Boundary — catches render crashes, falls back to simple map ───
class MapErrorBoundary extends Component<{
  children: React.ReactNode;
  fallbackLat: number | null;
  fallbackLng: number | null;
}, {
  hasError: boolean;
}> {
  constructor(props: any) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  componentDidCatch(error: Error) {
    console.error('[MAP-BOUNDARY] Crash caught:', error.message);
  }
  render() {
    if (this.state.hasError) {
      return <EmbeddedMap destinationLat={this.props.fallbackLat} destinationLng={this.props.fallbackLng} data-id="element-4015" />;
    }
    return this.props.children;
  }
}
// ─── Alarm System — loud repeating alert for new offers ──────────
// ─── Alarm System — loud repeating alert for new offers ──────────
// IMPORTANT: On mobile, AudioContext MUST be created/resumed during a user gesture.
// Call warmUp() on any user tap (e.g. "CONECTAR") to unlock audio playback.
function createAlarmAudio(): {
  play: () => void;
  stop: () => void;
  warmUp: () => void;
} {
  let audioCtx: AudioContext | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let isPlaying = false;
  // HTML5 Audio fallback for browsers where Web Audio API fails (some iOS versions)
  let fallbackAudio: HTMLAudioElement | null = null;
  function getCtx(): AudioContext | null {
    try {
      if (!audioCtx) {
        const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtxClass) return null;
        audioCtx = new AudioCtxClass();
      }
      if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
      }
      return audioCtx;
    } catch {
      return null;
    }
  }
  function beep() {
    const ctx = getCtx();
    if (!ctx) {
      // Fallback: use HTML5 Audio with a short beep tone (base64 WAV)
      try {
        if (!fallbackAudio) {
          // Tiny 440Hz beep WAV, 0.3s, base64 encoded
          fallbackAudio = new Audio('data:audio/wav;base64,UklGRl9vT19teleVBBQQAIAAABAAEARAAA' + 'AEQAAABAAgAZGF0YUlvT19/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/');
        }
        fallbackAudio.currentTime = 0;
        fallbackAudio.volume = 1.0;
        fallbackAudio.play().catch(() => {});
      } catch {}
      return;
    }
    try {
      // First tone — high pitch alarm
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.type = 'square';
      osc1.frequency.setValueAtTime(880, ctx.currentTime);
      osc1.frequency.setValueAtTime(1100, ctx.currentTime + 0.12);
      osc1.frequency.setValueAtTime(880, ctx.currentTime + 0.24);
      gain1.gain.setValueAtTime(0.7, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.4);
      // Second tone — slightly delayed, different pitch for urgency
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.type = 'square';
      osc2.frequency.setValueAtTime(1320, ctx.currentTime + 0.15);
      osc2.frequency.setValueAtTime(1100, ctx.currentTime + 0.3);
      gain2.gain.setValueAtTime(0, ctx.currentTime);
      gain2.gain.setValueAtTime(0.5, ctx.currentTime + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.45);
      osc2.start(ctx.currentTime + 0.15);
      osc2.stop(ctx.currentTime + 0.45);
    } catch (e) {
      console.warn('[ALARM] beep failed:', e);
    }
  }
  return {
    // Call this on a user gesture (tap) to unlock audio on ALL mobile browsers
    warmUp() {
      try {
        // 1. Warm up Web Audio API
        const ctx = getCtx();
        if (ctx) {
          const buffer = ctx.createBuffer(1, 1, 22050);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          source.start(0);
          console.log('[ALARM] AudioContext warmed up, state:', ctx.state);
        }
        // 2. Warm up HTML5 Audio fallback
        if (!fallbackAudio) {
          fallbackAudio = new Audio('data:audio/wav;base64,UklGRl9vT19teleVBBQQAIAAABAAEARAAA' + 'AEQAAABAAgAZGF0YUlvT19/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/');
        }
        fallbackAudio.volume = 0.01;
        fallbackAudio.play().then(() => {
          fallbackAudio!.pause();
          fallbackAudio!.volume = 1.0;
          console.log('[ALARM] HTML5 Audio fallback warmed up');
        }).catch(() => {});
        // 3. Warm up Speech Synthesis (required on iOS Safari)
        if ('speechSynthesis' in window) {
          const warmUtterance = new SpeechSynthesisUtterance('');
          warmUtterance.volume = 0;
          warmUtterance.lang = 'es-ES';
          window.speechSynthesis.speak(warmUtterance);
          // Pre-load voices
          window.speechSynthesis.getVoices();
          console.log('[ALARM] SpeechSynthesis warmed up');
        }
      } catch (e) {
        console.warn('[ALARM] warmUp failed:', e);
      }
    },
    play() {
      if (isPlaying) return;
      isPlaying = true;
      console.log('[ALARM] 🔊 Starting alarm');
      // Immediate first beep
      beep();
      // Repeat every 700ms for urgency
      intervalId = setInterval(() => {
        beep();
        // Vibrate (not supported on iOS Safari, but works on Android)
        if ('vibrate' in navigator) {
          navigator.vibrate([300, 100, 300, 100, 400]);
        }
      }, 700);
    },
    stop() {
      if (isPlaying) console.log('[ALARM] 🔇 Stopping alarm');
      isPlaying = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      // Stop vibration (Android only)
      if ('vibrate' in navigator) {
        navigator.vibrate(0);
      }
      // Stop any playing fallback audio
      if (fallbackAudio) {
        fallbackAudio.pause();
        fallbackAudio.currentTime = 0;
      }
    }
  };
}
// Request notification permission — must be called from user gesture on some browsers
function requestNotificationPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission().then(perm => {
      console.log('[NOTIFY] Permission result:', perm);
    });
  }
}
// Show system notification
function showOfferNotification() {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const notification = new Notification('🚗 ¡Nuevo viaje disponible!', {
        body: 'Tienes una nueva oferta de entrega. ¡Toca para aceptar!',
        tag: 'driver-offer',
        requireInteraction: true,
        vibrate: [300, 100, 300, 100, 400, 200, 500],
        silent: false
      } as any);
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      setTimeout(() => notification.close(), 30000);
    } catch (e) {
      console.warn('[NOTIFY] Failed:', e);
    }
  }
}
const alarmInstance = createAlarmAudio();
function useOfferAlert(offersCount: number) {
  const prevCount = useRef(offersCount);
  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);
  useEffect(() => {
    if (offersCount > prevCount.current) {
      console.log('[ALARM] New offer detected! Count:', offersCount);
      // 1. Strong vibration pattern
      if ('vibrate' in navigator) {
        navigator.vibrate([300, 100, 300, 100, 400, 200, 500]);
      }
      // 2. Loud repeating alarm sound
      alarmInstance.play();
      // 3. System notification (works in background)
      showOfferNotification();
    }
    // Stop alarm when offers are cleared (accepted/rejected/expired)
    if (offersCount === 0 && prevCount.current > 0) {
      alarmInstance.stop();
    }
    prevCount.current = offersCount;
  }, [offersCount]);
  return {
    stopAlarm: alarmInstance.stop,
    warmUpAudio: alarmInstance.warmUp
  };
}
// ─── Countdown Timer Component ───────────────────────────────────
function CountdownTimer({
  expiresAt
}: {
  expiresAt: string;
}) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      return Math.max(0, Math.floor(diff / 1000));
    };
    setSecondsLeft(calc());
    const interval = setInterval(() => setSecondsLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  const isUrgent = secondsLeft <= 10;
  const minutes = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  if (secondsLeft <= 0) {
    return <span className="text-red-400 font-bold text-lg" data-id="element-4016">Expirada</span>;
  }
  return <div className={`text-center ${isUrgent ? 'animate-pulse' : ''}`} data-id="element-4017">
      <div className={`text-5xl font-black tabular-nums ${isUrgent ? 'text-red-400' : 'text-white'}`} data-id="element-4018">
        {minutes > 0 ? `${minutes}:${secs.toString().padStart(2, '0')}` : `${secs}s`}
      </div>
      <p className="text-xs text-white/40 uppercase tracking-widest mt-1" data-id="element-4019">
        Tiempo restante
      </p>
    </div>;
}
// ─── Debug Panel Component ───────────────────────────────────────
function DriverDebugPanel({
  user,
  driverId,
  authDebug,
  offers,
  debugInfo,
  isOnline,
  onRefetch
}: {
  user: any;
  driverId: number | null;
  authDebug: any;
  offers: any[];
  debugInfo: any;
  isOnline: boolean;
  onRefetch: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return <div className="mx-5 mb-4" data-id="element-4020">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between gap-2 bg-[#1A1A1A] border border-white/10 rounded-xl px-4 py-2.5 text-xs" data-id="element-4021">
        <div className="flex items-center gap-2" data-id="element-4022">
          <Bug className="w-3.5 h-3.5 text-yellow-400" data-id="element-4023" />
          <span className="text-yellow-400 font-bold" data-id="element-4024">DEBUG</span>
          <span className="text-white/40" data-id="element-4025">
            driver={driverId || 'null'} | offers={offers.length} | bypass=
            {debugInfo.allOffersCount}
          </span>
        </div>
        {expanded ? <ChevronUp className="w-3.5 h-3.5 text-white/30" data-id="element-4026" /> : <ChevronDown className="w-3.5 h-3.5 text-white/30" data-id="element-4027" />}
      </button>

      {expanded && <div className="mt-1 bg-[#1A1A1A] border border-white/10 rounded-xl p-4 space-y-2 text-xs font-mono" data-id="element-4028">
          <div className="text-white/60" data-id="element-4029">
            <span className="text-white/30" data-id="element-4030">auth.uid: </span>
            <span className="text-cyan-400" data-id="element-4031">{authDebug.userId || 'null'}</span>
          </div>
          <div className="text-white/60" data-id="element-4032">
            <span className="text-white/30" data-id="element-4033">driverId: </span>
            <span className="text-cyan-400" data-id="element-4034">{driverId ?? 'null'}</span>
          </div>
          <div className="text-white/60" data-id="element-4035">
            <span className="text-white/30" data-id="element-4036">RPC result: </span>
            <span className="text-cyan-400" data-id="element-4037">
              {JSON.stringify(authDebug.rpcResult)}
            </span>
          </div>
          {authDebug.rpcError && <div className="text-red-400" data-id="element-4038">
              <span className="text-white/30" data-id="element-4039">RPC error: </span>
              {JSON.stringify(authDebug.rpcError)}
            </div>}
          <div className="text-white/60" data-id="element-4040">
            <span className="text-white/30" data-id="element-4041">isOnline: </span>
            <span className={isOnline ? 'text-green-400' : 'text-red-400'} data-id="element-4042">
              {String(isOnline)}
            </span>
          </div>

          <div className="border-t border-white/5 pt-2 mt-2" data-id="element-4043" />

          <div className="text-white/60" data-id="element-4044">
            <span className="text-white/30" data-id="element-4045">pending offers: </span>
            <span className={debugInfo.pendingCount > 0 ? 'text-green-400 font-bold' : 'text-red-400'} data-id="element-4046">
              {debugInfo.pendingCount}
            </span>
          </div>
          <div className="text-white/60" data-id="element-4047">
            <span className="text-white/30" data-id="element-4048">all offers (bypass): </span>
            <span className={debugInfo.allOffersCount > 0 ? 'text-yellow-400' : 'text-red-400'} data-id="element-4049">
              {debugInfo.allOffersCount}
            </span>
          </div>
          <div className="text-white/60" data-id="element-4050">
            <span className="text-white/30" data-id="element-4051">realtime: </span>
            <span className={debugInfo.realtimeActive ? 'text-green-400' : 'text-red-400'} data-id="element-4052">
              {debugInfo.realtimeActive ? 'SUBSCRIBED' : 'NOT SUBSCRIBED'}
            </span>
          </div>
          <div className="text-white/60" data-id="element-4053">
            <span className="text-white/30" data-id="element-4054">last fetch: </span>
            <span className="text-white/50" data-id="element-4055">
              {debugInfo.lastFetchTime || 'never'}
            </span>
          </div>
          {debugInfo.lastError && <div className="text-red-400" data-id="element-4056">
              <span className="text-white/30" data-id="element-4057">error: </span>
              {debugInfo.lastError}
            </div>}

          {/* Bypass offers detail */}
          {debugInfo.bypassOffers.length > 0 && <div className="border-t border-white/5 pt-2 mt-2" data-id="element-4058">
              <p className="text-white/30 mb-1" data-id="element-4059">All offers (no filter):</p>
              {debugInfo.bypassOffers.map((o: any) => {
          const expired = new Date(o.expires_at).getTime() < Date.now();
          const expiresIn = Math.round((new Date(o.expires_at).getTime() - Date.now()) / 1000);
          return <div key={o.id} className="text-white/50 pl-2" data-id="element-4060">
                    #{o.id}: {o.status}
                    {expired ? <span className="text-red-400" data-id="element-4061"> EXPIRED</span> : <span className="text-green-400" data-id="element-4062"> {expiresIn}s left</span>}
                  </div>;
        })}
            </div>}

          {debugInfo.allOffersCount === 0 && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2 mt-2" data-id="element-4063">
              <p className="text-red-400 font-bold" data-id="element-4064">⚠ RLS BLOCKING</p>
              <p className="text-red-400/70" data-id="element-4065">
                Bypass query returned 0 rows. Run RLS_DELIVERY_OFFERS_FIX.sql in
                Supabase.
              </p>
            </div>}

          <button onClick={onRefetch} className="w-full mt-2 flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-lg py-2 text-white/50 hover:text-white/80 active:scale-95 transition-all" data-id="element-4066">
            <RefreshCw className="w-3.5 h-3.5" data-id="element-4067" />
            Force Refetch
          </button>
        </div>}
    </div>;
}
// ─── Helper to extract job display data from real DB columns ─────
function getJobDisplayData(job: any) {
  if (!job) return {
    pickup: 'Restaurante',
    dropoff: 'Cliente',
    fee: 0,
    pickupLat: null,
    pickupLng: null,
    dropoffLat: null,
    dropoffLng: null,
    pickupName: 'Restaurante',
    dropoffName: 'Cliente',
    pickupAddress: '',
    dropoffAddress: '',
    customerPhone: null,
    mapsUrl: null
  };
  return {
    pickup: job.pickup_address_text || job.pickup_address || job.origin_address || job.pickup_location || job.restaurant_name || 'Restaurante',
    dropoff: job.dropoff_address_text || job.dropoff_address || job.destination_address || job.dropoff_location || job.customer_address || 'Cliente',
    fee: job.delivery_fee_estimated || job.delivery_fee || job.fee || job.estimated_fee || 0,
    pickupLat: job.pickup_lat || null,
    pickupLng: job.pickup_lng || null,
    dropoffLat: job.dropoff_lat || null,
    dropoffLng: job.dropoff_lng || null,
    pickupName: job.pickup_name || job.restaurant_name || 'Restaurante',
    dropoffName: job.dropoff_name || job.customer_name || 'Cliente',
    pickupAddress: job.pickup_address_text || job.pickup_address || job.origin_address || '',
    dropoffAddress: job.dropoff_address_text || job.dropoff_address || job.destination_address || '',
    customerPhone: job.customer_phone || job.dropoff_phone || job.phone || null,
    mapsUrl: job.maps_url || job.delivery_maps_url || null
  };
}
// ─── Main App ────────────────────────────────────────────────────
export function DriverAppPage() {
  const navigate = useNavigate();
  const {
    user,
    driverId,
    loading: authLoading,
    resolveError,
    logout,
    authDebug,
    retry: retryAuth
  } = useDriverAuth();
  const {
    isOnline,
    toggleStatus
  } = useDriverSession(driverId);
  const {
    offers,
    acceptOffer,
    rejectOffer,
    debugInfo,
    refetch
  } = useDriverOffers(driverId);
  const {
    activeTrip,
    updateTripStatus,
    clearTrip,
    refetchTrip
  } = useActiveTrip(driverId);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusToggling, setStatusToggling] = useState(false);
  const [tripError, setTripError] = useState<string | null>(null);
  const [deliveryComplete, setDeliveryComplete] = useState<{
    fee: number;
  } | null>(null);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [navigationMode, setNavigationMode] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{
    distance: number;
    duration: number;
    steps: RouteStep[];
  } | null>(null);
  // Alert on new offers (loud alarm + system notification)
  const {
    stopAlarm,
    warmUpAudio
  } = useOfferAlert(offers.length);
  // Auto-dismiss delivery complete toast
  useEffect(() => {
    if (!deliveryComplete) return;
    const timer = setTimeout(() => {
      setDeliveryComplete(null);
    }, 2500);
    return () => clearTimeout(timer);
  }, [deliveryComplete]);
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/driver/login');
    }
  }, [user, authLoading, navigate]);
  if (authLoading) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-4068">
        <div className="w-10 h-10 border-3 border-[#006847] border-t-transparent rounded-full animate-spin" data-id="element-4069" />
      </div>;
  }
  if (!driverId) {
    // Temporary error — show retryable state, NOT "Sin perfil"
    if (resolveError) {
      return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-8 text-center" data-id="element-4070">
          <WifiOff className="w-16 h-16 text-amber-500/40 mb-6" data-id="element-4071" />
          <p className="text-white text-xl font-bold mb-2" data-id="element-4072">Error de conexión</p>
          <p className="text-white/50 mb-8 max-w-xs" data-id="element-4073">{resolveError}</p>
          <button onClick={retryAuth} className="text-[#006847] font-bold text-lg py-3 px-8 border border-[#006847] rounded-xl flex items-center gap-2 mb-4" data-id="element-4074">
            <RefreshCw className="w-5 h-5" data-id="element-4075" />
            Reintentar
          </button>
          <button onClick={logout} className="text-white/30 text-sm py-2 px-6" data-id="element-4076">
            Cerrar sesión
          </button>
        </div>;
    }
    // Definitive — no driver profile found
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-8 text-center" data-id="element-4077">
        <Car className="w-16 h-16 text-white/20 mb-6" data-id="element-4078" />
        <p className="text-white text-xl font-bold mb-2" data-id="element-4079">
          Sin perfil de conductor
        </p>
        <p className="text-white/50 mb-8" data-id="element-4080">
          No tienes un perfil de conductor asignado a esta cuenta.
        </p>
        <button onClick={logout} className="text-[#006847] font-bold text-lg py-3 px-8 border border-[#006847] rounded-xl" data-id="element-4081">
          Cerrar sesión
        </button>
      </div>;
  }
  const handleLogout = async () => {
    if (isOnline) await toggleStatus(false);
    await logout();
  };
  const handleToggleStatus = async () => {
    setStatusToggling(true);
    // Warm up audio + request notification permission on user gesture (required by mobile browsers)
    if (!isOnline) {
      warmUpAudio();
      requestNotificationPermission();
    }
    await toggleStatus(!isOnline);
    setStatusToggling(false);
  };
  const handleAccept = async (offerId: number) => {
    setActionLoading(true);
    setOfferError(null);
    stopAlarm();
    console.log('[DRIVER-UI] 🚀 Accepting offer:', offerId);
    const result = await acceptOffer(offerId);
    console.log('[DRIVER-UI] Accept result:', result);
    if (!result || result.success === false) {
      const errorMsg = result?.error || 'Error desconocido al aceptar';
      console.error('[DRIVER-UI] ❌ Accept failed:', errorMsg);
      setOfferError(errorMsg);
      setTimeout(() => setActionLoading(false), 1000);
      return;
    }
    console.log('[DRIVER-UI] ✅ Accept succeeded — refreshing state');
    setOfferError(null);
    // Immediately fetch active trip to transition into trip screen
    console.log('[DRIVER-UI] 🔄 Post-accept: fetching active trip...');
    await refetchTrip();
    console.log('[DRIVER-UI] 🔄 Post-accept: active trip fetch complete');
    console.log('[DRIVER-UI] 🔄 Post-accept: activeTrip will update on next render');
    setActionLoading(false);
  };
  const handleReject = async (offerId: number) => {
    setActionLoading(true);
    stopAlarm();
    await rejectOffer(offerId);
    setActionLoading(false);
  };
  // ROLLBACK: Route callbacks disabled (NavigationMap not rendered)
  // const handleRouteError = useCallback((error: string) => { ... }, [])
  // const handleRouteUpdate = useCallback((data) => { ... }, [])
  // const handleRetryRoute = () => { ... }
  const handleStatusChange = async (action: 'arrived' | 'picked_up' | 'en_route' | 'delivered', jobId: number) => {
    console.log('[DRIVER-UI] ═══════════════════════════════════');
    console.log('[DRIVER-UI] Button clicked:', action);
    console.log('[DRIVER-UI] Job ID:', jobId);
    console.log('[DRIVER-UI] Current activeTrip status:', activeTrip?.status);
    console.log('[DRIVER-UI] Order ID on trip:', activeTrip?.order_id);
    // Capture fee before clearing trip
    const currentFee = activeTrip ? activeTrip.delivery_fee_estimated || activeTrip.delivery_fee || activeTrip.fee || 0 : 0;
    const orderId = activeTrip?.order_id;
    setActionLoading(true);
    setTripError(null);
    // Step 1: Update delivery_jobs via hook (RPC + fallback)
    const result = await updateTripStatus(action, jobId);
    console.log('[DRIVER-UI] updateTripStatus result:', result);
    if (result && !result.success) {
      console.error('[DRIVER-UI] ❌ Status change failed:', result.error);
      const errorMsg = action === 'picked_up' ? 'No se pudo actualizar el pedido. Intenta de nuevo.' : action === 'delivered' ? 'No se pudo marcar como entregado. Intenta de nuevo.' : result.error || 'Error actualizando estado';
      setTripError(errorMsg);
      setActionLoading(false);
      return;
    }
    // Step 2: DIRECT update to public.orders (belt-and-suspenders)
    // This guarantees the orders table is updated even if syncOrderFromDeliveryJob failed silently
    if (orderId && (action === 'picked_up' || action === 'delivered')) {
      const orderUpdatePayload: Record<string, any> = {};
      if (action === 'picked_up') {
        console.log('[DRIVER-UI] 📝 Updating order to picked_up — order_id:', orderId);
        orderUpdatePayload.status = 'out_for_delivery';
        orderUpdatePayload.picked_up_at = new Date().toISOString();
        if (driverId) {
          orderUpdatePayload.driver_id = driverId;
        }
      } else if (action === 'delivered') {
        console.log('[DRIVER-UI] 📝 Updating order to delivered — order_id:', orderId);
        orderUpdatePayload.status = 'delivered';
        orderUpdatePayload.delivered_at = new Date().toISOString();
        if (driverId) {
          orderUpdatePayload.driver_id = driverId;
        }
      }
      console.log('[DRIVER-UI] 📝 Direct orders update payload:', orderUpdatePayload);
      const {
        data: orderUpdateResult,
        error: orderUpdateError
      } = await supabase.from('orders').update(orderUpdatePayload).eq('id', orderId).select('id, status');
      if (orderUpdateError) {
        console.error('[DRIVER-UI] ❌ Supabase update error on public.orders:', orderUpdateError.message);
        console.error('[DRIVER-UI] Error details:', JSON.stringify(orderUpdateError));
        // Don't block the flow — the delivery_jobs update already succeeded
      } else {
        console.log('[DRIVER-UI] ✅ Supabase update success on public.orders:', JSON.stringify(orderUpdateResult));
      }
    } else if (!orderId) {
      console.warn('[DRIVER-UI] ⚠️ No order_id on activeTrip — cannot update public.orders directly');
    }
    // Chain picked_up -> en_route automatically to simplify UX
    if (action === 'picked_up' && result?.success) {
      console.log('[DRIVER-UI] 🔗 Chaining picked_up -> en_route');
      const chainResult = await updateTripStatus('en_route', jobId);
      if (chainResult && !chainResult.success) {
        console.warn('[DRIVER-UI] ⚠️ Chained en_route failed, but picked_up succeeded:', chainResult.error);
      }
      // ROLLBACK: Navigation mode activation disabled
      console.log('[DRIVER-UI] 🗺️ Picked up — step 3 active');
    }
    // On successful delivery: haptic feedback + show success
    if (action === 'delivered' && result?.success) {
      // Haptic feedback
      if ('vibrate' in navigator) {
        navigator.vibrate([100]);
      }
      console.log('[DRIVER-UI] ✅ Delivery complete — showing success');
      // Clear trip and show success toast
      clearTrip();
      setTripError(null);
      setActionLoading(false);
      setDeliveryComplete({
        fee: currentFee
      });
      // Refresh offers in background so driver is ready for next trip
      refetch();
      return;
    }
    setActionLoading(false);
    console.log('[DRIVER-UI] ═══════════════════════════════════');
  };
  // Priority offer = first pending offer
  const priorityOffer = offers.length > 0 ? offers[0] : null;
  // ─── SCREEN: OFFLINE ──────────────────────────────────────────
  if (!isOnline) {
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-4082">
        {/* Mini header */}
        <div className="flex items-center justify-between px-5 py-4" data-id="element-4083">
          <div className="flex items-center gap-2" data-id="element-4084">
            <Car className="w-5 h-5 text-white/30" data-id="element-4085" />
            <span className="font-bold text-white/50 text-sm" data-id="element-4086">
              NOIZA Driver
            </span>
          </div>
          <button onClick={handleLogout} className="p-2 text-white/30" data-id="element-4087">
            <LogOut className="w-5 h-5" data-id="element-4088" />
          </button>
        </div>

        {/* Big offline state */}
        <div className="flex-1 flex flex-col items-center justify-center px-8" data-id="element-4089">
          <div className="w-32 h-32 rounded-full bg-[#1A1A1A] border-4 border-white/10 flex items-center justify-center mb-8" data-id="element-4090">
            <WifiOff className="w-16 h-16 text-white/20" data-id="element-4091" />
          </div>
          <h2 className="text-3xl font-black text-white/30 mb-2" data-id="element-4092">
            DESCONECTADO
          </h2>
          <p className="text-white/30 text-center mb-12" data-id="element-4093">
            No estás recibiendo viajes
          </p>

          <button onClick={handleToggleStatus} disabled={statusToggling} className="w-full max-w-xs py-6 rounded-2xl bg-[#006847] text-white font-black text-2xl flex items-center justify-center gap-3 shadow-2xl shadow-[#006847]/30 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4094">
            {statusToggling ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-4095" /> : <>
                <Power className="w-8 h-8" data-id="element-4096" />
                CONECTAR
              </>}
          </button>
        </div>
      </div>;
  }
  // ─── SCREEN: DELIVERY COMPLETE (brief success) ─────────────────
  if (deliveryComplete) {
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center text-white px-8" data-id="element-4097">
        <div className="w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center mb-6 animate-[scale-in_0.3s_ease-out]" data-id="element-4098">
          <CheckCircle2 className="w-14 h-14 text-green-400" data-id="element-4099" />
        </div>
        <h2 className="text-3xl font-black text-white mb-2" data-id="element-4100">
          ¡Entrega completada!
        </h2>
        {deliveryComplete.fee > 0 && <p className="text-2xl font-bold text-green-400 mb-2" data-id="element-4101">
            {formatCurrency(deliveryComplete.fee, '₡')}
          </p>}
        <p className="text-white/40 text-sm" data-id="element-4102">Volviendo a buscar viajes...</p>
        <div className="mt-6 w-48 h-1 bg-white/10 rounded-full overflow-hidden" data-id="element-4103">
          <div className="h-full bg-green-500 rounded-full animate-[shrink_2.5s_linear_forwards]" data-id="element-4104" />
        </div>
      </div>;
  }
  // ─── SCREEN: ACTIVE TRIP (highest priority) ───────────────────
  if (activeTrip) {
    const tripData = getJobDisplayData(activeTrip);
    let step = 1;
    let progress = 33;
    let title = 'PASO 1 DE 3';
    let headerBadge = '🏪 Vas al restaurante';
    let destinationLabel = 'Dirección del restaurante';
    let destinationName = tripData.pickupName;
    let destinationAddress = tripData.pickupAddress;
    let mapLat = tripData.pickupLat;
    let mapLng = tripData.pickupLng;
    let mapsUrl = tripData.mapsUrl;
    if (activeTrip.status === 'arrived_pickup') {
      step = 2;
      progress = 66;
      title = 'PASO 2 DE 3';
      headerBadge = '📦 Ya llegaste al restaurante';
      destinationLabel = 'Dirección del cliente';
      destinationName = tripData.dropoffName;
      destinationAddress = tripData.dropoffAddress;
      mapLat = tripData.dropoffLat;
      mapLng = tripData.dropoffLng;
    } else if (activeTrip.status === 'picked_up' || activeTrip.status === 'en_route') {
      step = 3;
      progress = 100;
      title = 'PASO 3 DE 3';
      headerBadge = '🚗 Camino al cliente';
      destinationLabel = 'Dirección del cliente';
      destinationName = tripData.dropoffName;
      destinationAddress = tripData.dropoffAddress;
      mapLat = tripData.dropoffLat;
      mapLng = tripData.dropoffLng;
    }
    return <div className="bg-[#0B0B0B] flex flex-col text-white" style={{
      minHeight: '100dvh'
    }} data-id="element-4105">
        {/* Header */}
        <div className="bg-[#141414] border-b border-white/5 px-5 py-3 flex items-center justify-between" style={{
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }} data-id="element-4106">
          <div className="flex flex-col" data-id="element-4107">
            <span className="font-bold text-white/50 text-[10px] tracking-widest mb-0.5" data-id="element-4108">
              {title}
            </span>
            <span className="font-black text-white text-sm" data-id="element-4109">{headerBadge}</span>
          </div>
          <span className="text-2xl font-black text-[#00a870]" data-id="element-4110">
            {formatCurrency(tripData.fee, '₡')}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-1.5 w-full bg-[#1A1A1A]" data-id="element-4111">
          <div className="h-full bg-[#00a870] transition-all duration-500 ease-out" style={{
          width: `${progress}%`
        }} data-id="element-4112" />
        </div>

        {/* Error Banner */}
        {tripError && <div className="mx-4 mt-3 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3" data-id="element-4113">
            <span className="text-red-400 text-sm font-mono flex-1" data-id="element-4114">
              {tripError}
            </span>
            <button onClick={() => setTripError(null)} className="text-red-400/60 text-xs font-bold" data-id="element-4115">
              ✕
            </button>
          </div>}

        {/* Main Content */}
        {step === 3 || navigationMode /* ── NAVIGATION MODE — full-screen real-time map ── */ ? <div className="flex-1 flex flex-col overflow-hidden" data-id="element-4116">
            <div className="flex-1 relative" data-id="element-4117">
              {mapLat && mapLng ? <MapErrorBoundary fallbackLat={mapLat} fallbackLng={mapLng} data-id="element-4118">
                  <NavigationMap destinationLat={mapLat} destinationLng={mapLng} destinationName={destinationName} destinationAddress={destinationAddress} compact={false} data-id="element-4119" />
                </MapErrorBoundary> : <EmbeddedMap destinationLat={mapLat} destinationLng={mapLng} data-id="element-4120" />}
            </div>

            {/* Bottom Action Bar */}
            <div className="bg-[#141414] border-t border-white/10 px-4 pt-4 shrink-0" style={{
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))'
        }} data-id="element-4121">
              <div className="space-y-3" data-id="element-4122">
                {step === 1 && <button disabled={actionLoading} onClick={() => handleStatusChange('arrived', activeTrip.id)} className="w-full py-6 bg-white text-black font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4123">
                    {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-4124" /> : <>
                        <MapPin className="w-8 h-8" data-id="element-4125" /> YA LLEGUÉ
                      </>}
                  </button>}

                {step === 2 && <button disabled={actionLoading} onClick={() => handleStatusChange('picked_up', activeTrip.id)} className="w-full py-6 bg-[#006847] text-white font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#006847]/20" data-id="element-4126">
                    {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-4127" /> : <>
                        <Package className="w-8 h-8" data-id="element-4128" /> YA RECOGÍ EL PEDIDO
                      </>}
                  </button>}

                {step === 3 && <button disabled={actionLoading} onClick={() => handleStatusChange('delivered', activeTrip.id)} className="w-full py-6 bg-[#00a870] text-white font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#00a870]/30" data-id="element-4129">
                    {actionLoading ? <Loader2 className="w-8 h-8 animate-spin" data-id="element-4130" /> : <>
                        <CheckCircle2 className="w-8 h-8" data-id="element-4131" /> PEDIDO ENTREGADO
                      </>}
                  </button>}

                {mapLat && mapLng && <a href={`https://www.google.com/maps/dir/?api=1&destination=${mapLat},${mapLng}&travelmode=driving`} target="_blank" rel="noreferrer" className="w-full py-3 bg-transparent text-white/40 font-bold text-sm rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-transform" data-id="element-4132">
                    <MapIcon className="w-4 h-4" data-id="element-4133" /> Abrir en Google Maps
                  </a>}

                {step !== 3 && <button onClick={() => setNavigationMode(false)} className="w-full py-3 bg-transparent text-white/40 font-bold text-sm rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-transform" data-id="element-4134">
                    <MapIcon className="w-4 h-4" data-id="element-4135" /> Ver resumen
                  </button>}
              </div>
            </div>
          </div> /* ── STATIC MODE (Steps 1 & 2) — overview with "Iniciar viaje" button ── */ : <div className="flex-1 flex flex-col overflow-y-auto" data-id="element-4136">
            {/* Route Map — shows driver + destination + polyline via OSRM */}
            <div className="px-4 pt-4 h-64" data-id="element-4137">
              {mapLat && mapLng ? <MapErrorBoundary fallbackLat={mapLat} fallbackLng={mapLng} data-id="element-4138">
                  <StaticRouteMap destinationLat={mapLat} destinationLng={mapLng} onRouteData={setRouteInfo} data-id="element-4139" />
                </MapErrorBoundary> : <EmbeddedMap destinationLat={mapLat} destinationLng={mapLng} data-id="element-4140" />}
            </div>

            {/* ETA / Distance Card */}
            {routeInfo && <div className="px-4 pt-3" data-id="element-4141">
                <div className="bg-[#141414] border border-[#00a870]/20 rounded-2xl p-4 flex items-center justify-around" data-id="element-4142">
                  <div className="flex flex-col items-center" data-id="element-4143">
                    <Clock className="w-5 h-5 text-[#00a870] mb-1" data-id="element-4144" />
                    <p className="text-2xl font-black text-white tabular-nums" data-id="element-4145">
                      {Math.max(1, Math.round(routeInfo.duration / 60))}
                    </p>
                    <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mt-0.5" data-id="element-4146">
                      min
                    </p>
                  </div>
                  <div className="w-px h-12 bg-white/10" data-id="element-4147" />
                  <div className="flex flex-col items-center" data-id="element-4148">
                    <Navigation className="w-5 h-5 text-[#00a870] mb-1" data-id="element-4149" />
                    <p className="text-2xl font-black text-white tabular-nums" data-id="element-4150">
                      {routeInfo.distance >= 1000 ? (routeInfo.distance / 1000).toFixed(1) : Math.round(routeInfo.distance)}
                    </p>
                    <p className="text-[10px] text-white/40 font-bold uppercase tracking-wider mt-0.5" data-id="element-4151">
                      {routeInfo.distance >= 1000 ? 'km' : 'm'}
                    </p>
                  </div>
                </div>
              </div>}

            {/* Destination Card */}
            <div className="px-4 pt-4" data-id="element-4152">
              <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 text-center" data-id="element-4153">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-4154">
                  {step === 1 ? <MapPin className="w-4 h-4 text-white/40" data-id="element-4155" /> : <Navigation className="w-4 h-4 text-white/40" data-id="element-4156" />}
                  <p className="text-xs text-white/40 uppercase tracking-widest font-bold" data-id="element-4157">
                    {destinationLabel}
                  </p>
                </div>
                <p className="text-2xl font-black leading-tight mb-1" data-id="element-4158">
                  {destinationName || 'Destino'}
                </p>
                {destinationAddress && <p className="text-white/50 text-sm leading-relaxed" data-id="element-4159">
                    {destinationAddress}
                  </p>}
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-4" data-id="element-4160" />

            {/* Actions */}
            <div className="px-4 pt-4 space-y-3" style={{
          paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))'
        }} data-id="element-4161">
              {/* INICIAR VIAJE — activates real-time navigation */}
              {mapLat && mapLng && <button onClick={() => setNavigationMode(true)} className="w-full py-7 bg-[#006847] text-white font-black text-2xl rounded-3xl flex justify-center items-center gap-3 active:scale-95 transition-transform shadow-xl shadow-[#006847]/30" data-id="element-4162">
                  <Navigation className="w-8 h-8" data-id="element-4163" /> INICIAR VIAJE
                </button>}

              {step === 1 && <button disabled={actionLoading} onClick={() => handleStatusChange('arrived', activeTrip.id)} className="w-full py-5 bg-white text-black font-black text-xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4164">
                  {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-4165" /> : <>
                      <MapPin className="w-7 h-7" data-id="element-4166" /> YA LLEGUÉ
                    </>}
                </button>}

              {step === 2 && <button disabled={actionLoading} onClick={() => handleStatusChange('picked_up', activeTrip.id)} className="w-full py-5 bg-white/10 border border-white/20 text-white font-black text-xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4167">
                  {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-4168" /> : <>
                      <Package className="w-7 h-7" data-id="element-4169" /> YA RECOGÍ EL PEDIDO
                    </>}
                </button>}

              {step === 3 && <button disabled={actionLoading} onClick={() => handleStatusChange('delivered', activeTrip.id)} className="w-full py-6 bg-[#00a870] text-white font-black text-2xl rounded-2xl flex justify-center items-center gap-3 active:scale-95 transition-transform disabled:opacity-50 shadow-xl shadow-[#00a870]/30" data-id="element-4170">
                  {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-4171" /> : <>
                      <CheckCircle2 className="w-7 h-7" data-id="element-4172" /> PEDIDO ENTREGADO
                    </>}
                </button>}

              {mapLat && mapLng && <a href={`https://www.google.com/maps/dir/?api=1&destination=${mapLat},${mapLng}&travelmode=driving`} target="_blank" rel="noreferrer" className="w-full py-3 bg-transparent text-white/40 font-bold text-sm rounded-xl flex justify-center items-center gap-2 active:scale-95 transition-transform" data-id="element-4173">
                  <MapIcon className="w-4 h-4" data-id="element-4174" /> Abrir en Google Maps
                </a>}
            </div>
          </div>}
      </div>;
  }
  // ─── SCREEN: FULL-SCREEN OFFER (priority) ─────────────────────
  if (priorityOffer) {
    const job = priorityOffer.delivery_jobs;
    const jobData = getJobDisplayData(job);
    return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-4175">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[#141414] border-b border-white/5" data-id="element-4176">
          <div className="flex items-center gap-2" data-id="element-4177">
            <div className="w-3 h-3 rounded-full bg-[#006847] animate-pulse" data-id="element-4178" />
            <span className="font-bold text-sm text-[#00a870]" data-id="element-4179">
              NUEVA OFERTA
            </span>
          </div>
          <button onClick={handleToggleStatus} className="text-xs text-white/30 font-bold py-1.5 px-3 border border-white/10 rounded-lg" data-id="element-4180">
            Desconectar
          </button>
        </div>

        <div className="flex-1 flex flex-col px-5 py-6" data-id="element-4181">
          {/* Error Banner */}
          {offerError && <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-3" data-id="element-4182">
              <span className="text-red-400 text-sm font-bold flex-1" data-id="element-4183">
                {offerError}
              </span>
              <button onClick={() => setOfferError(null)} className="text-red-400/60 text-xs font-bold" data-id="element-4184">
                ✕
              </button>
            </div>}

          {/* Payment - Hero */}
          <div className="text-center mb-8" data-id="element-4185">
            <p className="text-6xl font-black text-white mb-1" data-id="element-4186">
              {formatCurrency(jobData.fee, '₡')}
            </p>
            <p className="text-white/40 text-sm" data-id="element-4187">Pago estimado</p>
          </div>

          {/* Countdown */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 mb-6 flex justify-center" data-id="element-4188">
            <CountdownTimer expiresAt={priorityOffer.expires_at} data-id="element-4189" />
          </div>

          {/* Route */}
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-5 space-y-5 mb-6" data-id="element-4190">
            <div className="flex gap-4 items-start" data-id="element-4191">
              <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0 mt-0.5" data-id="element-4192">
                <MapPin className="w-5 h-5 text-white/50" data-id="element-4193" />
              </div>
              <div data-id="element-4194">
                <p className="text-xs text-white/40 uppercase font-bold mb-1" data-id="element-4195">
                  Recoger
                </p>
                <p className="text-base font-bold" data-id="element-4196">{jobData.pickup}</p>
              </div>
            </div>
            <div className="border-t border-white/5" data-id="element-4197" />
            <div className="flex gap-4 items-start" data-id="element-4198">
              <div className="w-10 h-10 rounded-full bg-[#006847]/10 flex items-center justify-center shrink-0 mt-0.5" data-id="element-4199">
                <Navigation className="w-5 h-5 text-[#006847]" data-id="element-4200" />
              </div>
              <div data-id="element-4201">
                <p className="text-xs text-white/40 uppercase font-bold mb-1" data-id="element-4202">
                  Entregar
                </p>
                <p className="text-base font-bold" data-id="element-4203">{jobData.dropoff}</p>
              </div>
            </div>
          </div>

          {/* Remaining offers indicator */}
          {offers.length > 1 && <p className="text-center text-xs text-white/30 mb-4" data-id="element-4204">
              +{offers.length - 1}{' '}
              {offers.length - 1 === 1 ? 'oferta más' : 'ofertas más'} en cola
            </p>}
        </div>

        {/* Action Buttons - Fixed Bottom */}
        <div className="p-5 pb-8 bg-[#141414] border-t border-white/5 flex gap-3" data-id="element-4205">
          <button disabled={actionLoading} onClick={() => handleReject(priorityOffer.id)} className="flex-1 py-6 rounded-2xl bg-[#1A1A1A] border border-white/10 text-white/60 font-black text-xl flex justify-center items-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4206">
            {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-4207" /> : <>
                <X className="w-7 h-7" data-id="element-4208" /> NO
              </>}
          </button>
          <button disabled={actionLoading} onClick={() => handleAccept(priorityOffer.id)} className="flex-[2] py-6 rounded-2xl bg-[#006847] text-white font-black text-xl flex justify-center items-center gap-3 shadow-2xl shadow-[#006847]/30 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4209">
            {actionLoading ? <Loader2 className="w-7 h-7 animate-spin" data-id="element-4210" /> : <>
                <Check className="w-7 h-7" data-id="element-4211" /> ACEPTAR
              </>}
          </button>
        </div>
      </div>;
  }
  // ─── SCREEN: ONLINE, WAITING FOR OFFERS ───────────────────────
  return <div className="min-h-screen bg-[#0B0B0B] flex flex-col text-white" data-id="element-4212">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4" data-id="element-4213">
        <div className="flex items-center gap-3" data-id="element-4214">
          <div className="w-3 h-3 rounded-full bg-[#006847] animate-pulse" data-id="element-4215" />
          <span className="font-bold text-sm text-[#00a870]" data-id="element-4216">ONLINE</span>
        </div>
        <button onClick={handleLogout} className="p-2 text-white/30" data-id="element-4217">
          <LogOut className="w-5 h-5" data-id="element-4218" />
        </button>
      </div>

      {/* Big online indicator */}
      <div className="flex-1 flex flex-col items-center justify-center px-8" data-id="element-4219">
        <div className="relative mb-8" data-id="element-4220">
          <div className="w-40 h-40 rounded-full bg-[#006847]/5 flex items-center justify-center" data-id="element-4221">
            <div className="w-28 h-28 rounded-full bg-[#006847]/10 flex items-center justify-center animate-pulse" data-id="element-4222">
              <div className="w-20 h-20 rounded-full bg-[#006847]/20 flex items-center justify-center" data-id="element-4223">
                <Wifi className="w-10 h-10 text-[#00a870]" data-id="element-4224" />
              </div>
            </div>
          </div>
          {/* Ping animation */}
          <div className="absolute inset-0 rounded-full border-2 border-[#006847]/20 animate-ping" data-id="element-4225" />
        </div>

        <h2 className="text-2xl font-black text-white mb-2" data-id="element-4226">Buscando viajes</h2>
        <p className="text-white/40 text-center text-sm mb-4" data-id="element-4227">
          Mantén la app abierta para recibir ofertas
        </p>
      </div>

      {/* Debug Panel */}
      {/* Debug panel hidden — uncomment to re-enable:
                                                                                  <DriverDebugPanel
                                                                                    user={user}
                                                                                    driverId={driverId}
                                                                                    authDebug={authDebug}
                                                                                    offers={offers}
                                                                                    debugInfo={debugInfo}
                                                                                    isOnline={isOnline}
                                                                                    onRefetch={refetch}
                                                                                  />
                                                                                  */}

      {/* Disconnect button at bottom */}
      <div className="p-5 pb-8" data-id="element-4228">
        <button onClick={handleToggleStatus} disabled={statusToggling} className="w-full py-5 rounded-2xl bg-[#1A1A1A] border border-white/10 text-white/50 font-bold text-lg flex items-center justify-center gap-3 active:scale-95 transition-transform disabled:opacity-50" data-id="element-4229">
          {statusToggling ? <Loader2 className="w-6 h-6 animate-spin" data-id="element-4230" /> : <>
              <Power className="w-6 h-6" data-id="element-4231" />
              DESCONECTAR
            </>}
        </button>
      </div>
    </div>;
}